/**
 * The product question on `/start` (#2630).
 *
 * "What does your team's day look like?" Six answers with one example
 * line each, then a confirmation screen. It runs BEFORE sign-up, so the
 * list comes from the static mirror in `lib/products.ts` and the offer
 * state from the API. A product that is not offered shows
 * "We are building this." with an optional email field and starts nothing.
 *
 * The chosen key goes to `tarii.signup_product` (see `lib/signupProduct`)
 * and survives the Clerk sign-up. Team creation sends it as
 * `product_preset`; the API decides the team kind and the components.
 */
import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { Button } from "../components/ui";
import {
  PRODUCTS,
  PRODUCT_BUILDING_PLAIN,
  PRODUCT_EMAIL_BAD,
  PRODUCT_EMAIL_NUDGE,
  PRODUCT_NOTED,
  PRODUCT_NOTIFY_BUTTON,
  PRODUCT_NOTIFY_THANKS,
  PRODUCT_SAVE_FAILED,
  type ProductKey,
  SOMETHING_ELSE,
  emailLooksRight,
  postProductAnswer,
} from "../lib/products";

export const PRODUCT_QUESTION = "What does your team's day look like?";
export const PICK_ANOTHER_ANSWER = "Pick another answer";

/**
 * A closed answer (#2651): the building line, one optional email field and
 * a button. The press records the pick, with the email when there is one.
 */
function ClosedAnswer({ productKey }: { productKey: ProductKey }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "bad" | "failed" | "thanks" | "noted">(
    "idle",
  );
  // An empty email asks once (#2698); the next press records without one.
  const [nudged, setNudged] = useState(false);
  if (state === "thanks" || state === "noted") {
    return (
      <p className="mt-2 text-fg text-sm" data-testid={`product-thanks-${productKey}`}>
        {state === "thanks" ? PRODUCT_NOTIFY_THANKS : PRODUCT_NOTED}
      </p>
    );
  }
  const send = async () => {
    const given = email.trim();
    if (given && !emailLooksRight(given)) {
      setState("bad");
      return;
    }
    if (!given && !nudged) {
      setNudged(true);
      setState("idle");
      return;
    }
    setState("sending");
    try {
      await postProductAnswer(productKey, { email: given });
      setState(given ? "thanks" : "noted");
    } catch {
      setState("failed");
    }
  };
  return (
    <div className="mt-2 flex w-full flex-col gap-2" data-testid={`product-building-${productKey}`}>
      <p className="text-fg-subtle text-sm">{PRODUCT_BUILDING_PLAIN}</p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        maxLength={320}
        placeholder="Your email (optional)"
        aria-label="Your email (optional)"
        data-testid={`product-email-${productKey}`}
        className="w-full h-11 rounded-md border border-border-strong bg-bg px-3 text-base text-fg"
      />
      <Button
        size="sm"
        variant="secondary"
        className="h-11 self-start px-4 text-sm"
        data-testid={`product-notify-${productKey}`}
        disabled={state === "sending"}
        onClick={() => void send()}
      >
        {PRODUCT_NOTIFY_BUTTON}
      </Button>
      {nudged && !email.trim() && state === "idle" ? (
        <output className="block text-fg-muted text-sm" data-testid={`product-nudge-${productKey}`}>
          {PRODUCT_EMAIL_NUDGE}
        </output>
      ) : null}
      {state === "bad" || state === "failed" ? (
        <p role="alert" className="text-danger text-sm" data-testid={`product-error-${productKey}`}>
          {state === "bad" ? PRODUCT_EMAIL_BAD : PRODUCT_SAVE_FAILED}
        </p>
      ) : null}
    </div>
  );
}

export function ProductQuestion({
  picked,
  isOffered,
  onPick,
  onBack,
  onConfirm,
}: {
  /** The answer picked so far; null shows the question. */
  picked: ProductKey | null;
  isOffered: (key: ProductKey) => boolean;
  /** `note` is the "Something else" line. */
  onPick: (key: ProductKey, note?: string) => void;
  /** "Pick another answer" and the back link. */
  onBack: () => void;
  onConfirm: (key: ProductKey) => void;
}) {
  const [otherText, setOtherText] = useState("");
  const product = PRODUCTS.find((p) => p.key === picked) ?? null;

  if (product && isOffered(product.key)) {
    return (
      <section data-testid="product-confirm" className="mt-6 sm:mt-9">
        <h1 className="max-w-[22ch] font-semibold text-[2rem] text-fg leading-[1.1] tracking-tight sm:text-[2.5rem]">
          We will set up Tarii as {product.name}
        </h1>
        <ul className="mt-4 flex flex-col gap-2 text-base text-fg-muted leading-relaxed">
          {product.startsWith.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            size="md"
            className="h-11"
            variant="primary"
            data-testid="product-confirm-continue"
            onClick={() => onConfirm(product.key)}
          >
            Continue
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            size="md"
            variant="secondary"
            className="h-11"
            data-testid="product-pick-another"
            onClick={onBack}
          >
            {PICK_ANOTHER_ANSWER}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="product-question" className="mt-9">
      <h1 className="max-w-[22ch] font-semibold text-[2rem] text-fg leading-[1.1] tracking-tight sm:text-[2.5rem]">
        {PRODUCT_QUESTION}
      </h1>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2">
        {PRODUCTS.filter((p) => p.listed).map((p) => {
          const offered = isOffered(p.key);
          return (
            <li
              key={p.key}
              data-testid={`product-answer-${p.key}`}
              className="flex flex-col items-start gap-1.5 rounded-md border border-border-strong p-5"
            >
              <span className="font-semibold text-fg text-lg tracking-tight">{p.answer}</span>
              <span className="text-fg-muted text-sm leading-relaxed">{p.example}</span>
              {offered ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="mt-2 h-11 px-4 text-sm"
                  data-testid={`product-pick-${p.key}`}
                  onClick={() => {
                    postProductAnswer(p.key).catch(() => undefined);
                    onPick(p.key);
                  }}
                >
                  Choose this
                </Button>
              ) : (
                <ClosedAnswer productKey={p.key} />
              )}
            </li>
          );
        })}
        <li
          data-testid="product-answer-other"
          className="flex flex-col items-start gap-1.5 rounded-md border border-border-strong p-5"
        >
          <span className="font-semibold text-fg text-lg tracking-tight">
            {SOMETHING_ELSE.answer}
          </span>
          <label className="w-full text-fg-muted text-sm leading-relaxed">
            {SOMETHING_ELSE.example}
            <input
              type="text"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              maxLength={200}
              data-testid="product-other-text"
              className="mt-2 h-11 w-full rounded-md border border-border-strong bg-bg px-3 text-base text-fg"
            />
          </label>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2 h-11 px-4 text-sm"
            data-testid="product-pick-other"
            onClick={() => {
              postProductAnswer("other", { note: otherText }).catch(() => undefined);
              onPick(SOMETHING_ELSE.startsKey, otherText);
            }}
          >
            Choose this
          </Button>
        </li>
      </ul>
      <p className="mt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-11 items-center text-accent-fg text-sm underline underline-offset-4"
        >
          Back
        </button>
      </p>
    </section>
  );
}
